import React, { memo } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { GridRowParams, GridRowId } from '@mui/x-data-grid-premium';
import { OmnisearchDataGrid } from 'components/OmnisearchDataGrid';
import { FilterGroup } from 'components/Omnisearch/FilterPopover';
import { OmnisearchGridColDef, FilterKey } from 'hooks/useOmnisearchDatagrid';
import { TypedDocumentNode } from '@apollo/client';
import type { Row } from './types';

interface GridSectionProps {
  title: string;
  description: string;
  slots: {
    noRowsOverlay: React.ComponentType;
  };
  persistenceKey: string;
  columns: OmnisearchGridColDef<Row>[];
  dataQuery: TypedDocumentNode<any, any>;
  defaultSearch: string;
  getItems: (data: any) => Row[];
  getCount: (data: any) => number;
  checkboxSelection: boolean;
  selectedIds: string[] | undefined;
  onSelectedRowsChanged: ((rows: Row[]) => void) | undefined;
  onSelectedIdsChanged: ((ids: GridRowId[]) => void) | undefined;
  onRowClick: (params: GridRowParams<Row>) => void;
  getRowLink?: (row: Row) => string | null;
  commandButtons: React.ReactNode[];
  columnVisibilityModel: Record<string, boolean>;
  additionalFilterColumns: FilterKey[];
  excludeFilterColumns: string[];
  filterGroups: FilterGroup[];
  tenantId: string | undefined;
}

/**
 * Memoized grid section to prevent re-renders when parent state changes
 * that don't affect the grid (e.g., stats toggle, chart selections for display only)
 */
export const GridSection = memo(function GridSection({
  title,
  description,
  slots,
  persistenceKey,
  columns,
  dataQuery,
  defaultSearch,
  getItems,
  getCount,
  checkboxSelection,
  selectedIds,
  onSelectedRowsChanged,
  onSelectedIdsChanged,
  onRowClick,
  getRowLink,
  commandButtons,
  columnVisibilityModel,
  additionalFilterColumns,
  excludeFilterColumns,
  filterGroups,
  tenantId,
}: GridSectionProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        pb: 3,
      }}
    >
      <Box sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={1}
        >
          <Box>
            <Typography
              variant="h5"
              component="h5"
              color="text.primary"
              pb={1}
            >
              {title}
            </Typography>
            <Typography fontSize={12} component="p">
              {description}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box>
        <OmnisearchDataGrid
          slots={slots}
          hideEmptyOverlayWhileLoading
          persistenceKey={persistenceKey}
          isURLDriven
          columns={columns}
          dataQuery={dataQuery}
          defaultSearch={defaultSearch}
          getItems={getItems}
          getCount={getCount}
          checkboxSelection={checkboxSelection}
          selectedIds={selectedIds}
          onSelectedRowsChanged={onSelectedRowsChanged}
          onSelectedIdsChanged={onSelectedIdsChanged}
          onRowClick={onRowClick}
          getRowLink={getRowLink}
          commandButtons={commandButtons}
          initialPageSize={50}
          rowHeight="auto"
          columnVisibilityModel={columnVisibilityModel}
          additionalFilterColumns={additionalFilterColumns}
          excludeFilterColumns={excludeFilterColumns}
          filterGroups={filterGroups}
          tenantId={tenantId}
        />
      </Box>
    </Paper>
  );
});
